import React, { useState, useEffect, useRef } from 'react';
import { Plus, LayoutDashboard, GitBranch, Activity, CreditCard, Settings, BookOpen, MessageCircle, AlertTriangle, Sparkles, Pencil, Check, X } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { RadioGroup, RadioGroupItem } from './ui/radio-group';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from './ui/select';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogDescription,
  DialogFooter 
} from './ui/dialog';
import { Tabs, TabsList, TabsTrigger } from './ui/tabs';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './ui/tooltip';
import { Badge } from './ui/badge';
import logo from '../assets/images/turret-dark.svg';
import { buildApiUrl } from '../config/api';

interface ProjectLimitError {
  current_plan: string;
  max_projects: number;
  projects_count: number;
  upgrade_to: string | null;
  upgrade_plan_name: string | null;
  upgrade_max_projects: number | null;
}

interface Organization {
  id: number;
  name: string;
  role: string;
  projects: Project[];
}

interface Project {
  id: number;
  name: string;
  api_key: string;
  organization: {
    id: number;
    name: string;
  };
}

interface User {
  id: number;
  email: string;
  name: string;
  organizations: Organization[];
}

interface SidenavProps {
  user: User;
  token: string;
  currentView: string;
  onViewChange: (view: string) => void;
  currentProject: Project | null;
  onProjectChange: (project: Project) => void;
}

export default function Sidenav({ 
  user, 
  token, 
  currentView, 
  onViewChange, 
  currentProject, 
  onProjectChange 
}: SidenavProps) {
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [showNewProjectDialog, setShowNewProjectDialog] = useState(false);
  const [newProjectName, setNewProjectName] = useState('');
  const [selectedOrganization, setSelectedOrganization] = useState<string>('');
  const [creatingProject, setCreatingProject] = useState(false);
  const [apiStatus, setApiStatus] = useState<'online' | 'offline' | 'checking'>('checking');
  const [showUpgradeDialog, setShowUpgradeDialog] = useState(false);
  const [projectLimitError, setProjectLimitError] = useState<ProjectLimitError | null>(null);
  
  // Project rename state
  const [editingProjectId, setEditingProjectId] = useState<number | null>(null);
  const [editingProjectName, setEditingProjectName] = useState('');
  const [savingProject, setSavingProject] = useState(false);
  const editInputRef = useRef<HTMLInputElement>(null);
  


  // Get all projects from user's organizations
  useEffect(() => {
    fetchProjects();
  }, [token]);

  // Set default selected organization (first org)
  useEffect(() => {
    if (user.organizations.length > 0 && !selectedOrganization) {
      setSelectedOrganization(user.organizations[0].id.toString());
    }
  }, [user.organizations, selectedOrganization]);

  // Check API status
  const checkApiStatus = async () => {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 5000); // 5 second timeout
      
      const response = await fetch(buildApiUrl('api/v1/projects'), {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        signal: controller.signal
      });
      
      clearTimeout(timeoutId);
      
      if (response.ok) {
        setApiStatus('online');
      } else {
        setApiStatus('offline');
      }
    } catch (error) {
      console.error('API status check failed:', error);
      setApiStatus('offline');
    }
  };

  // Check API status on mount and periodically
  useEffect(() => {
    checkApiStatus();
    const interval = setInterval(checkApiStatus, 30000); // Check every 30 seconds
    return () => clearInterval(interval);
  }, [token]);

  const fetchProjects = async () => {
    try {
      setLoading(true);
      const response = await fetch(buildApiUrl('api/v1/projects'), {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch projects');
      }
      
      const data = await response.json();
      setProjects(data.data.projects);
      
      // Try to restore project from localStorage first
      if (!currentProject && data.data.projects.length > 0) {
        const savedProjectId = localStorage.getItem('turret_current_project_id');
        let projectToSet = null;
        
        if (savedProjectId) {
          // Try to find the saved project
          projectToSet = data.data.projects.find((p: Project) => p.id.toString() === savedProjectId);
        }
        
        // If no saved project found or saved project doesn't exist, use first project
        if (!projectToSet) {
          projectToSet = data.data.projects[0];
        }
        
        onProjectChange(projectToSet);
      }
    } catch (error) {
      console.error('Error fetching projects:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateProject = async () => {
    if (!newProjectName.trim() || !selectedOrganization) return;
    
    try {
      setCreatingProject(true);
      const response = await fetch(buildApiUrl('api/v1/projects'), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          project: {
            name: newProjectName.trim()
          },
          organization_id: parseInt(selectedOrganization)
        })
      });
      
      const data = await response.json();
      
      // Check for project limit error
      if (!response.ok) {
        if (data.code === 'PROJECT_LIMIT_REACHED') {
          setProjectLimitError({
            current_plan: data.current_plan,
            max_projects: data.max_projects,
            projects_count: data.projects_count,
            upgrade_to: data.upgrade_to,
            upgrade_plan_name: data.upgrade_plan_name,
            upgrade_max_projects: data.upgrade_max_projects
          });
          setShowNewProjectDialog(false);
          setShowUpgradeDialog(true);
          return;
        }
        throw new Error(data.error || 'Failed to create project');
      }
      
      const newProject = data.data.project;
      
      // Add new project to the list
      setProjects(prev => [...prev, newProject]);
      
      // Switch to the new project
      onProjectChange(newProject);
      
      // Close dialog and reset form
      setShowNewProjectDialog(false);
      setNewProjectName('');
      setSelectedOrganization(user.organizations[0].id.toString());
      
    } catch (error) {
      console.error('Error creating project:', error);
    } finally {
      setCreatingProject(false);
    }
  };

  const handleUpgradeClick = () => {
    setShowUpgradeDialog(false);
    setProjectLimitError(null);
    onViewChange('billing');
  };

  const handleContactSales = () => {
    setShowUpgradeDialog(false);
    setProjectLimitError(null);
    window.open('mailto:sales@useturret.com?subject=Enterprise%20Plan%20Inquiry', '_blank');
  };

  const handleNewProjectClick = () => {
    setShowNewProjectDialog(true);
    setNewProjectName('');
    setSelectedOrganization(user.organizations[0].id.toString());
  };

  const handleStartEditProject = (project: Project, e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    setEditingProjectId(project.id);
    setEditingProjectName(project.name);
    // Focus input after state update
    setTimeout(() => editInputRef.current?.focus(), 50);
  };

  const handleCancelEditProject = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    setEditingProjectId(null);
    setEditingProjectName('');
  };

  const handleSaveProjectName = async (projectId: number, e?: React.MouseEvent | React.KeyboardEvent) => {
    if (e) {
      e.stopPropagation();
      e.preventDefault();
    }
    
    if (!editingProjectName.trim() || savingProject) return;
    
    const project = projects.find(p => p.id === projectId);
    if (!project || project.name === editingProjectName.trim()) {
      setEditingProjectId(null);
      setEditingProjectName('');
      return;
    }
    
    try {
      setSavingProject(true);
      const response = await fetch(buildApiUrl(`api/v1/projects/${projectId}`), {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          project: {
            name: editingProjectName.trim()
          }
        })
      });
      
      if (!response.ok) {
        throw new Error('Failed to update project');
      }
      
      const data = await response.json();
      const updatedProject = data.data.project;
      
      // Update projects list
      setProjects(prev => prev.map(p => 
        p.id === projectId ? { ...p, name: updatedProject.name } : p
      ));
      
      // Update current project if it was renamed
      if (currentProject?.id === projectId) {
        onProjectChange({ ...currentProject, name: updatedProject.name });
      }
      
      setEditingProjectId(null);
      setEditingProjectName('');
    } catch (error) {
      console.error('Error updating project:', error);
    } finally {
      setSavingProject(false);
    }
  };

  const handleEditKeyDown = (e: React.KeyboardEvent, projectId: number) => {
    if (e.key === 'Enter') {
      handleSaveProjectName(projectId, e);
    } else if (e.key === 'Escape') {
      setEditingProjectId(null);
      setEditingProjectName('');
    }
  };

  // Check if user is admin of a specific project's organization
  const isAdminOfProject = (project: Project): boolean => {
    const org = user.organizations.find(o => o.id === project.organization.id);
    return org?.role === 'admin';
  };



  const tabItems = [
    { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
    { id: 'journeys', label: 'Journeys', icon: GitBranch },
    { id: 'events', label: 'Events', icon: Activity },
    { id: 'billing', label: 'Billing', icon: CreditCard },
    { id: 'settings', label: 'Settings', icon: Settings },
  ];

  const currentOrg = user.organizations[0];
  const isAdmin = currentOrg?.role === 'admin';

  const getStatusColor = () => {
    switch (apiStatus) {
      case 'online':
        return 'bg-green-500';
      case 'offline':
        return 'bg-red-500';
      case 'checking':
        return 'bg-yellow-500';
      default:
        return 'bg-gray-500';
    }
  };

  const getStatusText = () => {
    switch (apiStatus) {
      case 'online':
        return 'API online';
      case 'offline':
        return 'API offline';
      case 'checking':
        return 'Checking status...';
      default:
        return 'Unknown status';
    }
  };

  const getTooltipText = () => {
    switch (apiStatus) {
      case 'online':
        return 'API is online and responding';
      case 'offline':
        return 'API is offline or unreachable';
      case 'checking':
        return 'Checking API connectivity...';
      default:
        return 'API status unknown';
    }
  };

  return (
    <>
      <div className="w-64 bg-card border-r border-border h-screen sticky top-0 flex flex-col">
        <div className="p-4 border-b border-border flex justify-between items-center">
          <div className="flex items-center">
            <img src={logo} alt="Turret Logo" className="h-8 w-8" />
            <h1 className="text-2xl font-bold ml-2">Turret</h1>
          </div>
          
          {/* Status Indicator */}
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <div className="flex items-center space-x-2">
                  <div className="relative">
                    <div 
                      className={`w-2 h-2 rounded-full ${getStatusColor()} ${
                        apiStatus === 'online' ? 'animate-pulse' : ''
                      }`}
                    />
                    {apiStatus === 'online' && (
                      <div className="absolute inset-0 w-2 h-2 rounded-full bg-green-500 animate-ping opacity-75" />
                    )}
                  </div>
                  <span className="text-xs text-muted-foreground font-medium">
                    {getStatusText()}
                  </span>
                </div>
              </TooltipTrigger>
              <TooltipContent>
                <p className="text-xs">{getTooltipText()}</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
        {/* Project Selector */}
        <div className="p-4 border-b border-border">
          <Label className="text-xs font-medium text-muted-foreground mb-2 block">
            Current Project
          </Label>
          {editingProjectId === currentProject?.id ? (
            <div className="flex items-center gap-1">
              <input
                ref={editInputRef}
                type="text"
                value={editingProjectName}
                onChange={(e) => setEditingProjectName(e.target.value)}
                onKeyDown={(e) => handleEditKeyDown(e, currentProject!.id)}
                className="flex-1 h-10 px-3 text-sm border border-border rounded-md bg-background focus:outline-none focus:ring-2 focus:ring-primary"
                disabled={savingProject}
                autoFocus
              />
              <button
                onClick={(e) => handleSaveProjectName(currentProject!.id, e)}
                disabled={savingProject || !editingProjectName.trim()}
                className="h-10 w-10 flex items-center justify-center hover:bg-accent rounded-md text-green-600 hover:text-green-700 disabled:opacity-50 border border-border"
              >
                <Check className="h-4 w-4" />
              </button>
              <button
                onClick={handleCancelEditProject}
                disabled={savingProject}
                className="h-10 w-10 flex items-center justify-center hover:bg-accent rounded-md text-muted-foreground hover:text-foreground border border-border"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          ) : (
            <div className="flex items-center gap-1">
              <Select
                value={currentProject?.id.toString() || ''}
                onValueChange={(value) => {
                  const project = projects.find(p => p.id.toString() === value);
                  if (project) onProjectChange(project);
                }}
              >
                <SelectTrigger className="flex-1">
                  <SelectValue placeholder="Select a project" />
                </SelectTrigger>
                <SelectContent>
                  {projects.map((project) => (
                    <SelectItem key={project.id} value={project.id.toString()}>
                      <div className="flex flex-col justify-center">
                        <span className="pt-0.5 font-medium text-left text-sm leading-snug truncate max-w-[180px]">{project.name}</span>
                        <span className="text-[10px] text-left text-muted-foreground leading-normal">
                          {project.organization.name}
                        </span>
                      </div>
                    </SelectItem>
                  ))}
                  <div className="border-t border-border mt-1 pt-1">
                    <button
                      onClick={handleNewProjectClick}
                      className="flex items-center space-x-2 w-full px-2 py-1.5 text-sm text-primary hover:bg-accent hover:text-accent-foreground rounded-sm"
                    >
                      <Plus className="h-4 w-4" />
                      <span>New Project</span>
                    </button>
                  </div>
                </SelectContent>
              </Select>
              {currentProject && isAdminOfProject(currentProject) && (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button
                        onClick={(e) => handleStartEditProject(currentProject, e)}
                        className="h-10 w-10 flex items-center justify-center hover:bg-accent rounded-md text-muted-foreground hover:text-foreground border border-border transition-colors"
                      >
                        <Pencil className="h-4 w-4" />
                      </button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p className="text-xs">Rename project</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}
            </div>
          )}
        </div>

        {/* Navigation Tabs */}
        <div className="flex-1 p-4">
          <Tabs value={currentView} onValueChange={onViewChange} orientation="vertical">
            <TabsList className="grid w-full grid-cols-1 h-auto bg-transparent space-y-1">
              {tabItems.map((item) => {
                const Icon = item.icon;
                return (
                  <TabsTrigger
                    key={item.id}
                    value={item.id}
                    className="w-full py-2 justify-start data-[state=active]:bg-primary data-[state=active]:text-primary-foreground hover:bg-accent hover:text-accent-foreground"
                  >
                    <Icon className="h-4 w-4 mr-2" />
                    {item.label}
                  </TabsTrigger>
                );
              })}
            </TabsList>
          </Tabs>
          
        </div>

        {/* External Links */}
        <div className="px-4 py-2 border-border">
          <div className="grid w-full grid-cols-1 h-auto bg-transparent space-y-1 text-sm font-medium">
            <a
              href="https://docs.useturret.com"
              target="_blank"
              rel="noopener noreferrer"
              className="w-full py-2 justify-start hover:text-accent-foreground rounded-md flex items-center px-3 text-gray-500 hover:text-neutral-800 hover:bg-accent transition-colors"
            >
              <BookOpen className="h-4 w-4 mr-2" />
              Documentation
            </a>
            <a
              href="https://x.com/evansjohnpaul"
              target="_blank"
              rel="noopener noreferrer"
              className="w-full py-2 justify-start hover:text-accent-foreground rounded-md flex items-center px-3 text-gray-500 hover:text-neutral-800 hover:bg-accent transition-colors"
            >
              <MessageCircle className="h-4 w-4 mr-2" />
              Support
            </a>
          </div>
        </div>

        {/* User & Organization Info */}
        <div className="pl-4 py-4 border-t border-border">
          <div className="text-xs text-muted-foreground p-2">
            <div className="font-medium">{user.name}</div>
            <div className="mt-1">
              {currentOrg && (
                <span>{currentOrg.name}</span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* New Project Dialog */}
      <Dialog open={showNewProjectDialog} onOpenChange={setShowNewProjectDialog}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Create New Project</DialogTitle>
            <DialogDescription>
              Create a new project to organize your events and analytics.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="project-name">Project Name</Label>
              <Input
                id="project-name"
                placeholder="Enter project name"
                value={newProjectName}
                onChange={(e) => setNewProjectName(e.target.value)}
                disabled={creatingProject}
              />
            </div>
            
            <div className="space-y-3">
              <Label>Select Organization</Label>
              <RadioGroup 
                value={selectedOrganization} 
                onValueChange={setSelectedOrganization}
                disabled={creatingProject}
              >
                {user.organizations.map((org) => (
                  <div key={org.id} className="flex items-center space-x-2">
                    <RadioGroupItem value={org.id.toString()} id={`org-${org.id}`} />
                    <Label htmlFor={`org-${org.id}`} className="font-normal">
                      {org.name}
                      {org.role === 'admin' && (
                        <span className="text-xs text-muted-foreground ml-2">
                          (Admin)
                        </span>
                      )}
                    </Label>
                  </div>
                ))}
              </RadioGroup>
            </div>
          </div>
          
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setShowNewProjectDialog(false)}
              disabled={creatingProject}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleCreateProject}
              disabled={!newProjectName.trim() || !selectedOrganization || creatingProject}
            >
              {creatingProject ? 'Creating...' : 'Create Project'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Upgrade Dialog - Project Limit Reached */}
      <Dialog open={showUpgradeDialog} onOpenChange={setShowUpgradeDialog}>
        <DialogContent className="sm:max-w-[450px]">
          <DialogHeader>
            <div className="flex items-center space-x-3 mb-2">
              <div className="p-2 bg-amber-100 rounded-full">
                <AlertTriangle className="h-5 w-5 text-amber-600" />
              </div>
              <DialogTitle className="text-xl">Project Limit Reached</DialogTitle>
            </div>
            <DialogDescription className="text-base">
              You've reached the maximum number of projects for your current plan.
            </DialogDescription>
          </DialogHeader>
          
          {projectLimitError && (
            <div className="space-y-4 py-4">
              {/* Current Plan Info */}
              <div className="bg-muted/50 rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-muted-foreground">Current Plan</span>
                  <Badge variant="secondary" className="capitalize">
                    {projectLimitError.current_plan}
                  </Badge>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Projects Used</span>
                  <span className="font-medium">
                    {projectLimitError.projects_count} / {projectLimitError.max_projects}
                  </span>
                </div>
              </div>

              {/* Upgrade Option */}
              {projectLimitError.upgrade_to && projectLimitError.upgrade_to !== 'enterprise' ? (
                <div className="border border-primary/20 bg-primary/5 rounded-lg p-4">
                  <div className="flex items-start space-x-3">
                    <Sparkles className="h-5 w-5 text-primary mt-0.5" />
                    <div className="flex-1">
                      <h4 className="font-semibold text-primary">
                        Upgrade to {projectLimitError.upgrade_plan_name}
                      </h4>
                      <p className="text-sm text-muted-foreground mt-1">
                        Get up to {projectLimitError.upgrade_max_projects} projects, plus more events and longer data retention.
                      </p>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="border border-purple-200 bg-purple-50 rounded-lg p-4">
                  <div className="flex items-start space-x-3">
                    <Sparkles className="h-5 w-5 text-purple-600 mt-0.5" />
                    <div className="flex-1">
                      <h4 className="font-semibold text-purple-800">
                        Need More Projects?
                      </h4>
                      <p className="text-sm text-purple-700 mt-1">
                        Contact us about our Enterprise plan for unlimited projects, unlimited events, and dedicated support.
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
          
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button 
              variant="outline" 
              onClick={() => {
                setShowUpgradeDialog(false);
                setProjectLimitError(null);
              }}
              className="w-full sm:w-auto"
            >
              Maybe Later
            </Button>
            {projectLimitError?.upgrade_to && projectLimitError.upgrade_to !== 'enterprise' ? (
              <Button 
                onClick={handleUpgradeClick}
                className="w-full sm:w-auto"
              >
                <Sparkles className="h-4 w-4 mr-2" />
                View Upgrade Options
              </Button>
            ) : (
              <Button 
                onClick={handleContactSales}
                className="w-full sm:w-auto bg-purple-600 hover:bg-purple-700"
              >
                Contact Sales
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </>
  );
} 